// src/services/calendarServices.js - Browser-Compatible Calendar Integration Services
import { OutlookCalendarService } from './outlookCalendar'
import { localDateString, outlookDateTime, newCalendarEntries } from '../lib/calendarDates'
import { GoogleCalendarService } from './googleCalendar'
export { OutlookCalendarService } from './outlookCalendar'
export { GoogleCalendarService } from './googleCalendar'

// Unified Calendar Service
export class CalendarService {
  constructor(userId) {
    this.googleService = new GoogleCalendarService(userId)
    this.outlookService = new OutlookCalendarService(userId)
  }

  async initialize() {
    await Promise.all([
      this.googleService.initialize(),
      this.outlookService.initialize()
    ])
  }

  // Convert calendar events to time entries format
  convertEventsToTimeEntries(events, source = 'google') {
    return events.filter(event => source === 'outlook'
      ? !event.isAllDay && !event.isCancelled && event.responseStatus?.response !== 'declined'
      : !!event.start?.dateTime && event.status !== 'cancelled'
    ).map(event => {
      let title, startTime, endTime, description

      if (source === 'google') {
        title = event.summary || 'Untitled Event'
        startTime = new Date(event.start.dateTime || event.start.date)
        endTime = new Date(event.end.dateTime || event.end.date)
        description = event.description || ''
      } else if (source === 'outlook') {
        title = event.subject || 'Untitled Event'
        startTime = outlookDateTime(event.start)
        endTime = outlookDateTime(event.end)
        description = event.bodyPreview || ''
      }

      // Calculate duration in minutes
      const durationMinutes = Math.round((endTime - startTime) / (1000 * 60))
      if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return null

      // Categorize the activity based on title/description
      const category = this.categorizeActivity(title, description)
      const strategicValue = this.assessStrategicValue(title, description, category)

      return {
        activity_title: title,
        date: localDateString(startTime),
        start_at: startTime.toISOString(),
        end_at: endTime.toISOString(),
        duration_minutes: durationMinutes,
        category,
        strategic_value: strategicValue,
        source: `${source}_calendar`,
        external_id: event.id,
        notes: `${startTime.toTimeString().slice(0, 5)} - ${endTime.toTimeString().slice(0, 5)}`
      }
    }).filter(Boolean)
  }

  categorizeActivity(title, description) {
    const text = `${title} ${description}`.toLowerCase()

    // Define categorization rules
    const categories = {
      'Strategic Planning': ['strategy', 'planning', 'roadmap', 'okr', 'vision', 'quarterly'],
      'Product Development': ['development', 'coding', 'review', 'sprint', 'standup', 'technical'],
      'Customer Research': ['customer', 'user', 'research', 'interview', 'feedback', 'survey'],
      'Team Management': ['1:1', 'one on one', 'team meeting', 'performance', 'hiring', 'onboarding'],
      'Stakeholder Communication': ['stakeholder', 'executive', 'board', 'presentation', 'update'],
      'Operations': ['operational', 'process', 'workflow', 'admin', 'maintenance'],
      'Learning': ['training', 'learning', 'course', 'workshop', 'conference', 'education'],
      'Meetings': ['meeting', 'call', 'sync', 'discussion', 'review']
    }

    for (const [category, keywords] of Object.entries(categories)) {
      if (keywords.some(keyword => text.includes(keyword))) {
        return category
      }
    }

    return 'Other'
  }

  assessStrategicValue(title, description, category) {
    const text = `${title} ${description}`.toLowerCase()

    // High strategic value indicators
    const highValueKeywords = ['strategy', 'roadmap', 'vision', 'okr', 'executive', 'customer', 'product', 'planning']
    // Medium strategic value indicators
    const mediumValueKeywords = ['team', 'development', 'review', 'research', 'stakeholder']
    // Low strategic value indicators
    const lowValueKeywords = ['admin', 'operational', 'maintenance', 'routine']

    if (highValueKeywords.some(keyword => text.includes(keyword))) {
      return 'High'
    } else if (lowValueKeywords.some(keyword => text.includes(keyword))) {
      return 'Low'
    } else if (mediumValueKeywords.some(keyword => text.includes(keyword))) {
      return 'Medium'
    }

    // Default based on category
    const highValueCategories = ['Strategic Planning', 'Customer Research']
    const lowValueCategories = ['Operations', 'Meetings']

    if (highValueCategories.includes(category)) {
      return 'High'
    } else if (lowValueCategories.includes(category)) {
      return 'Low'
    }

    return 'Medium'
  }

  // Import events from both services for a date range
  async importEvents(startDate, endDate, selectedSources = ['google', 'outlook'], outlookCalendarId = '') {
    const allTimeEntries = []

    if (selectedSources.includes('google') && this.googleService.isAuthenticated) {
      const googleEvents = await this.googleService.getEvents(startDate, endDate)
      allTimeEntries.push(...this.convertEventsToTimeEntries(googleEvents, 'google'))
    }

    if (selectedSources.includes('outlook') && this.outlookService.isAuthenticated) {
      const outlookEvents = await this.outlookService.getEvents(startDate, endDate, outlookCalendarId)
      allTimeEntries.push(...this.convertEventsToTimeEntries(outlookEvents, 'outlook'))
    }

    // Keep distinct meetings on the same day, even if their titles match.
    const uniqueEntries = this.removeDuplicateEntries(allTimeEntries)
    return uniqueEntries
  }

  removeDuplicateEntries(entries) {
    return newCalendarEntries(entries)
  }
}

// Export service instance
export const calendarService = new CalendarService()
